import { t } from "../../i18n";
import { useEffect, useMemo, useRef, useState } from "react";
import { ArrowDown, ArrowLeft, ArrowRight, ArrowUp, GripVertical, Printer, Route, Wifi } from "lucide-react";
import type { Category, Product, Setup } from "../../types";
import { useBackHandler } from "../../backNavigation";
import {
  categoryPrinterFallbackIds,
  categoryPrinterRoutes,
  categoryPrinterRoutingEnabled
} from "../../printRouting";
import {
  printerName,
  optionLabels,
  splitCounters,
  splitOptions,
  splitVariations,
  variationMultiplier
} from "../../utils";

type Props = {
  back: React.ReactNode;
  setup: Setup;
  busy: boolean;
  onSaveSetup: (next: Setup) => void;
  onTestPrint: (printerId: number) => void;
  onProbePrinter: (printerId: number) => void;
};

type Profile = "order" | "history";
type PreviewType = "initial" | "supplementary" | "cancel" | "correction" | "history";
type CategoryOrderDraft = { order: number[]; pinned: number[] };

const PREVIEW_TYPES: Array<{ value: PreviewType; label: string }> = [
  { value: "initial", label: "Αρχική παραγγελία" },
  { value: "supplementary", label: "Συμπληρωματική" },
  { value: "cancel", label: "Ακύρωση" },
  { value: "correction", label: "Διόρθωση" },
  { value: "history", label: "Επανεκτύπωση" }
];

/* Key composition copied from MainActivity: printProfilePrinterKey(key, profile, printerId)
   = key [+ "_history"] [+ "_printer_<id>"]. Route and paper width are printer-scoped only. */
const HISTORY_SUFFIX = "_history";

function profileKey(key: string, profile: Profile): string {
  return profile === "history" ? key + HISTORY_SUFFIX : key;
}

function printerKey(key: string, printerId: number): string {
  return printerId > 0 ? `${key}_printer_${printerId}` : key;
}

function profilePrinterKey(key: string, profile: Profile, printerId: number): string {
  return printerKey(profileKey(key, profile), printerId);
}

const SELECTS: {
  key: string;
  label: string;
  scope: "profile" | "printer";
  fallback: string;
  options: { value: string; label: string }[];
}[] = [
  {
    key: "print_route",
    label: "Τρόπος εκτύπωσης",
    scope: "printer",
    fallback: "server_first",
    options: [
      { value: "server_first", label: "Server πρώτα, άμεση εκτύπωση αν αποτύχει" },
      { value: "direct", label: "Άμεσα στον εκτυπωτή" },
      { value: "server_only", label: "Μόνο μέσω server" }
    ]
  },
  {
    key: "print_paper_width",
    label: "Πλάτος χαρτιού",
    scope: "printer",
    fallback: "80",
    options: [
      { value: "80", label: "80mm" },
      { value: "58", label: "58mm" }
    ]
  },
  {
    key: "print_text_size",
    label: "Μέγεθος γραμμάτων",
    scope: "profile",
    fallback: "normal",
    options: [
      { value: "small", label: "Μικρό" },
      { value: "normal", label: "Κανονικό" },
      { value: "large", label: "Μεγάλο" }
    ]
  },
  {
    key: "print_detail_text_size",
    label: "Μέγεθος Extra / Χωρίς / σχολίων",
    scope: "profile",
    fallback: "normal",
    options: [
      { value: "normal", label: "Κανονικό" },
      { value: "large", label: "Μεγάλο" },
      { value: "extra_large", label: "Πολύ μεγάλο" }
    ]
  },
  {
    key: "print_item_spacing",
    label: "Κενό ανάμεσα στα προϊόντα",
    scope: "profile",
    fallback: "normal",
    options: [
      { value: "compact", label: "Συμπαγές" },
      { value: "normal", label: "Κανονικό" },
      { value: "wide", label: "Άνετο" }
    ]
  },
  {
    key: "print_item_order",
    label: "Σειρά προϊόντων στο χαρτί",
    scope: "profile",
    fallback: "selection",
    options: [
      { value: "selection", label: "Με τη σειρά επιλογής" },
      { value: "category", label: "Ανά κατηγορία, χωρίς headers" },
      { value: "category_headers", label: "Ανά κατηγορία, με headers" }
    ]
  },
  {
    key: "print_header_style",
    label: "Κεφαλίδα",
    scope: "profile",
    fallback: "table_code",
    options: [
      { value: "table_code", label: "Τραπέζι αριστερά, κωδικός δεξιά" },
      { value: "event_title", label: "Τύπος δελτίου πρώτα" }
    ]
  }
];

const TOGGLES: { key: string; label: string; fallback: boolean }[] = [
  { key: "print_show_order_code", label: "Κωδικός παραγγελίας (#)", fallback: true },
  { key: "print_show_table", label: "Τραπέζι", fallback: true },
  { key: "print_show_time", label: "Ώρα", fallback: true },
  { key: "print_show_waiter", label: "Σερβιτόρος", fallback: true },
  { key: "print_show_event_title", label: "Τύπος δελτίου", fallback: true },
  { key: "print_show_item_notes", label: "Λεπτομέρειες προϊόντων", fallback: true },
  { key: "print_show_general_notes", label: "Γενικό σχόλιο", fallback: true },
  { key: "print_show_separators", label: "Διαχωριστικές γραμμές", fallback: true },
  { key: "print_show_item_price", label: "Τιμή σε κάθε προϊόν", fallback: false },
  { key: "print_show_total", label: "Τελικό ποσό στο τέλος", fallback: false }
];

const QUANTITY_TOGGLES: { key: string; label: string; fallback: boolean }[] = [
  { key: "print_variation_in_qty", label: "Σε παρένθεση μετά το x, π.χ. 2x (1,5)", fallback: true },
  { key: "print_hide_unit_variation", label: "Απόκρυψη επιλογής (1)", fallback: true }
];

const TEXT_FIELDS: { key: string; label: string; fallback: string }[] = [
  { key: "print_item_template", label: "Μορφή γραμμής προϊόντος", fallback: "{qty}x {name}" },
  { key: "print_label_table", label: "Label τραπεζιού", fallback: "ΤΡΑΠΕΖΙ" },
  { key: "print_label_time", label: "Label ώρας", fallback: "Ώρα" },
  { key: "print_label_waiter", label: "Label σερβιτόρου", fallback: "Σερβιτόρος" },
  { key: "print_label_comment", label: "Label σχολίου", fallback: "Σχόλιο" },
  { key: "print_label_total", label: "Label συνόλου", fallback: "Σύνολο" },
  { key: "print_label_footer", label: "Κείμενο τέλους", fallback: "ΤΕΛΟΣ" },
  { key: "print_note_prefix", label: "Πρόθεμα λεπτομερειών", fallback: "  - " },
  { key: "print_separator", label: "Διαχωριστική γραμμή", fallback: "--------------------------------" }
];

type PreviewLine = {
  product: Product;
  quantity: number;
  name: string;
  notes: string;
  unitPrice: number;
};

function ticketMoney(value: number): string {
  return `${value.toFixed(2)} €`;
}

function printableTitle(previewType: PreviewType): string {
  if (previewType === "supplementary") return "ΣΥΜΠΛ. ΠΑΡΑΓΓΕΛΙΑ";
  if (previewType === "cancel") return "ΑΚΥΡΩΣΗ ΠΑΡΑΓΓ.";
  if (previewType === "correction") return "ΔΙΟΡΘ. ΠΑΡΑΓΓΕΛΙΑΣ";
  if (previewType === "history") return "ΕΠΑΝΕΚΤΥΠΩΣΗ";
  return "ΠΑΡΑΓΓΕΛΙΑ";
}

function printableFooterTitle(title: string): string {
  if (title === "ΣΥΜΠΛ. ΠΑΡΑΓΓΕΛΙΑ") return "ΣΥΜΠΛ. ΠΑΡΑΓΓ.";
  if (title === "ΠΑΡΑΓΓΕΛΙΑ") return "ΠΑΡΑΓΓΕΛΙΑΣ";
  if (title === "ΕΠΑΝΕΚΤΥΠΩΣΗ") return "ΕΠΑΝΕΚΤΥΠΩΣΗΣ";
  return title;
}

function previewProductNotes(product: Product): string {
  const notes: string[] = [];
  const without = splitOptions(product.without_options)[0];
  const extra = optionLabels(product.extras)[0];
  const counter = splitCounters(product.counter_options)[0];
  if (without) notes.push(`Χωρίς: ${without}`);
  if (extra) notes.push(`Extra: ${extra}`);
  if (counter) notes.push(`Μετρητές: ${counter.label} 1`);
  return notes.length ? notes.join(" | ") : "Σχόλιο: preview";
}

function previewProductName(
  product: Product,
  variation: string,
  hideUnitVariation: boolean,
  variationInQuantity: boolean
): string {
  const base = product.print_name?.trim() || product.name;
  if (!variation) return base;
  if (!/^[0-9][0-9.,/ ]*$/.test(variation)) return `${base} - ${variation}`;
  if (hideUnitVariation && variationMultiplier(variation) === 1) return base;
  return variationInQuantity ? `(${variation}) ${base}` : `${base} - ${variation}`;
}

function previewItemRow(
  line: PreviewLine,
  template: string,
  showNotes: boolean,
  showItemPrice: boolean
): string {
  const lineTotal = ticketMoney(line.quantity * line.unitPrice);
  let row = template
    .replaceAll("{qty}", String(line.quantity))
    .replaceAll("{name}", line.name)
    .replaceAll("{notes}", showNotes ? line.notes : "")
    .replaceAll("{price}", line.unitPrice > 0 ? ticketMoney(line.unitPrice) : "")
    .replaceAll("{line_total}", line.unitPrice > 0 ? lineTotal : "")
    .trim();
  if (showItemPrice
      && line.unitPrice > 0
      && !template.includes("{price}")
      && !template.includes("{line_total}")) {
    row += `  -  ${lineTotal}`;
  }
  return row;
}

function reorderIds(order: number[], movingId: number, targetId: number): number[] {
  const from = order.indexOf(movingId);
  const to = order.indexOf(targetId);
  if (from < 0 || to < 0 || from === to) return order;
  const next = order.slice();
  next.splice(from, 1);
  next.splice(to, 0, movingId);
  return next;
}

function PrintCategoryOrder({
  title,
  categories,
  initialOrder,
  initialPinned,
  onChange
}: {
  title: string;
  categories: Category[];
  initialOrder: number[];
  initialPinned: number[];
  onChange: (order: number[], pinnedLastIds: number[]) => void;
}) {
  const [order, setOrder] = useState(initialOrder);
  const [draggingId, setDraggingId] = useState<number | null>(null);
  const orderRef = useRef(initialOrder);
  const pinnedRef = useRef(new Set(initialPinned));
  const listRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<{ id: number; active: boolean; timer: number | null; pointerId: number } | null>(null);

  useEffect(() => {
    setOrder(initialOrder);
    orderRef.current = initialOrder;
    pinnedRef.current = new Set(initialPinned);
  }, [initialOrder.join(","), initialPinned.join(",")]);

  const ordered = useMemo(() => {
    const byId = new Map(categories.map((category) => [category.id, category]));
    return order.map((id) => byId.get(id)).filter((category): category is Category => Boolean(category));
  }, [categories, order]);

  const applyOrder = (next: number[], pinned = pinnedRef.current): void => {
    const normalized = [
      ...next.filter((id) => !pinned.has(id)),
      ...next.filter((id) => pinned.has(id))
    ];
    pinnedRef.current = new Set(pinned);
    orderRef.current = normalized;
    setOrder(normalized);
    onChange(normalized, normalized.filter((id) => pinned.has(id)));
  };

  const moveBy = (id: number, offset: number): void => {
    const index = order.indexOf(id);
    const target = Math.max(0, Math.min(order.length - 1, index + offset));
    if (index < 0 || target === index) return;
    const next = order.slice();
    next.splice(index, 1);
    next.splice(target, 0, id);
    const pinned = new Set(pinnedRef.current);
    pinned.delete(id);
    applyOrder(next, pinned);
  };

  const moveRelative = (id: number, value: string): void => {
    const next = order.filter((candidate) => candidate !== id);
    const pinned = new Set(pinnedRef.current);
    if (value === "first") {
      pinned.delete(id);
      next.unshift(id);
      applyOrder(next, pinned);
      return;
    }
    if (value === "last") {
      pinned.add(id);
      next.push(id);
      applyOrder(next, pinned);
      return;
    }
    const [direction, rawTarget] = value.split(":");
    const targetId = Number(rawTarget);
    const targetIndex = next.indexOf(targetId);
    if (pinned.has(targetId)) pinned.add(id); else pinned.delete(id);
    next.splice(Math.max(0, targetIndex + (direction === "after" ? 1 : 0)), 0, id);
    applyOrder(next, pinned);
  };

  return (
    <section className="settings-group print-category-order">
      <h2>{title}</h2>
      <div className="print-category-list" ref={listRef}>
        {ordered.map((category, index) => (
          <article className={`print-category-row ${draggingId === category.id ? "dragging" : ""}`} data-print-category-id={category.id} key={category.id}>
            <button
              type="button"
              className="icon-button drag-handle"
              aria-label={t("Μετακίνηση {0}", category.name)}
              title={t("Κρατήστε και σύρετε")}
              onPointerDown={(event) => {
                event.currentTarget.setPointerCapture(event.pointerId);
                const timer = window.setTimeout(() => {
                  if (dragRef.current?.id === category.id) {
                    dragRef.current.active = true;
                    const pinned = new Set(pinnedRef.current);
                    pinned.delete(category.id);
                    pinnedRef.current = pinned;
                    setDraggingId(category.id);
                    navigator.vibrate?.(15);
                  }
                }, 260);
                dragRef.current = { id: category.id, active: false, timer, pointerId: event.pointerId };
              }}
              onPointerMove={(event) => {
                const drag = dragRef.current;
                if (!drag?.active || drag.pointerId !== event.pointerId) return;
                event.preventDefault();
                const list = listRef.current;
                if (list) {
                  const bounds = list.getBoundingClientRect();
                  const edge = Math.min(72, bounds.height / 3);
                  if (event.clientY < bounds.top + edge) list.scrollBy(0, -18);
                  if (event.clientY > bounds.bottom - edge) list.scrollBy(0, 18);
                }
                const target = document.elementFromPoint(event.clientX, event.clientY)?.closest<HTMLElement>("[data-print-category-id]");
                const targetId = Number(target?.dataset.printCategoryId);
                if (targetId && targetId !== drag.id) {
                  const next = reorderIds(orderRef.current, drag.id, targetId);
                  orderRef.current = next;
                  setOrder(next);
                }
              }}
              onPointerUp={() => {
                const drag = dragRef.current;
                if (drag?.timer) window.clearTimeout(drag.timer);
                if (drag?.active) applyOrder(orderRef.current);
                dragRef.current = null;
                setDraggingId(null);
              }}
              onPointerCancel={() => {
                const drag = dragRef.current;
                if (drag?.timer) window.clearTimeout(drag.timer);
                if (drag?.active) applyOrder(orderRef.current);
                dragRef.current = null;
                setDraggingId(null);
              }}
            >
              <GripVertical />
            </button>
            <span className="print-category-copy">
              <strong>{category.name}</strong>
              {pinnedRef.current.has(category.id) ? <small>{t("Πάντα τελευταία")}</small> : null}
            </span>
            <div className="admin-row-actions compact-actions">
              <button type="button" className="icon-button" disabled={index === 0} onClick={() => moveBy(category.id, -1)} aria-label={t("Πάνω")}><ArrowUp /></button>
              <button type="button" className="icon-button" disabled={index === ordered.length - 1} onClick={() => moveBy(category.id, 1)} aria-label={t("Κάτω")}><ArrowDown /></button>
              <label className="position-select">
                <span className="sr-only">{t("Θέση ")}{category.name}</span>
                <select value="" onChange={(event) => { moveRelative(category.id, event.target.value); event.target.value = ""; }}>
                  <option value="" disabled>{t("Θέση...")}</option>
                  <option value="first">{t("Πρώτη")}</option>
                  <option value="last">{t("Πάντα τελευταία")}</option>
                  {ordered.filter((target) => target.id !== category.id).flatMap((target) => [
                    <option value={`before:${target.id}`} key={`before-${target.id}`}>{t("Πριν από ")}{target.name}</option>,
                    <option value={`after:${target.id}`} key={`after-${target.id}`}>{t("Μετά από ")}{target.name}</option>
                  ])}
                </select>
              </label>
            </div>
          </article>
        ))}
      </div>
    </section>
  );
}

export function PrintPage({ back, setup, busy, onSaveSetup, onTestPrint, onProbePrinter }: Props) {
  const [profile, setProfile] = useState<Profile>("order");
  const printerOptions = useMemo(
    () => setup.printers.slice().sort((a, b) => a.sort_order - b.sort_order || a.id - b.id),
    [setup.printers]
  );
  const [printerId, setPrinterId] = useState(printerOptions[0]?.id ?? 0);
  const [previewType, setPreviewType] = useState<PreviewType>("supplementary");
  const [categoryOrderDraft, setCategoryOrderDraft] = useState<CategoryOrderDraft | null>(null);
  useBackHandler(Boolean(categoryOrderDraft), () => setCategoryOrderDraft(null));
  /* Text fields edit locally and commit on blur: writing straight through would POST the whole
     setup on every keystroke. */
  const [drafts, setDrafts] = useState<Record<string, string>>({});

  const settings = new Map(setup.settings.map((row) => [row.key, row.value]));

  useEffect(() => {
    if (!printerOptions.some((printer) => printer.id === printerId)) {
      setPrinterId(printerOptions[0]?.id ?? 0);
    }
  }, [printerId, printerOptions]);

  useEffect(() => {
    setPreviewType(profile === "history" ? "history" : "supplementary");
    setCategoryOrderDraft(null);
  }, [profile, printerId]);

  const readKey = (key: string, scope: "profile" | "printer", fallback: string): string => {
    const base = scope === "printer" ? key : profileKey(key, profile);
    const scoped = printerKey(base, printerId);
    if (profile === "history" && scope === "profile") {
      // Android History inherits this printer's Order value before falling back to shared keys.
      return settings.get(scoped)
        ?? settings.get(base)
        ?? settings.get(printerKey(key, printerId))
        ?? settings.get(key)
        ?? fallback;
    }
    // Most specific wins, then the shared value for that scope, then the plain key.
    return settings.get(scoped) ?? settings.get(base) ?? settings.get(key) ?? fallback;
  };

  const writeKey = (key: string, scope: "profile" | "printer", value: string): void => {
    const target =
      scope === "printer" ? printerKey(key, printerId) : profilePrinterKey(key, profile, printerId);
    const next = setup.settings.filter((row) => row.key !== target);
    next.push({ key: target, value });
    onSaveSetup({ ...setup, settings: next });
  };

  const writeProfileValues = (values: Record<string, string>): void => {
    const targets = new Map(Object.entries(values).map(([key, value]) => [
      profilePrinterKey(key, profile, printerId),
      value
    ]));
    const next = setup.settings.filter((row) => !targets.has(row.key));
    for (const [key, value] of targets) {
      next.push({ key, value });
    }
    onSaveSetup({ ...setup, settings: next });
  };

  const writeGlobalValues = (values: Record<string, string>): void => {
    const targets = new Map(Object.entries(values));
    const next = setup.settings.filter((row) => !targets.has(row.key));
    for (const [key, value] of targets) next.push({ key, value });
    onSaveSetup({ ...setup, settings: next });
  };

  const readBool = (key: string, fallback: boolean): boolean =>
    readKey(key, "profile", fallback ? "1" : "0") === "1";

  const selectedPrinter = printerOptions.find((printer) => printer.id === printerId);
  const routeEnabled = categoryPrinterRoutingEnabled(setup);
  const categoryRoutes = categoryPrinterRoutes(setup);
  const fallbackPrinterIds = categoryPrinterFallbackIds(setup);
  const toggleRoutePrinter = (categoryId: number, targetPrinterId: number, checked: boolean): void => {
    const key = String(categoryId);
    const current = categoryRoutes[key] ?? [];
    const nextIds = checked
      ? [...new Set([...current, targetPrinterId])]
      : current.filter((id) => id !== targetPrinterId);
    const nextRoutes = { ...categoryRoutes, [key]: nextIds };
    writeGlobalValues({ category_printer_routes: JSON.stringify(nextRoutes) });
  };
  const itemOrderMode = readKey("print_item_order", "profile", "selection");
  const categoryIds = readKey("print_category_order", "profile", "")
    .split(",")
    .map(Number)
    .filter((id) => id > 0);
  const defaultCategories = setup.categories.slice().sort((a, b) => a.sort_order - b.sort_order || a.id - b.id);
  const validCategoryIds = new Set(defaultCategories.map((category) => category.id));
  const pinnedCategoryIds = readKey("print_categories_last", "profile", "")
    .split(",")
    .map(Number)
    .filter((id) => validCategoryIds.has(id));
  const pinnedCategorySet = new Set(pinnedCategoryIds);
  const baseCategoryIds = [
    ...categoryIds.filter((id) => defaultCategories.some((category) => category.id === id)),
    ...defaultCategories.map((category) => category.id).filter((id) => !categoryIds.includes(id))
  ];
  const resolvedCategoryIds = [
    ...baseCategoryIds.filter((id) => !pinnedCategorySet.has(id)),
    ...baseCategoryIds.filter((id) => pinnedCategorySet.has(id))
  ];
  const categoryRank = new Map(resolvedCategoryIds.map((id, index) => [id, index]));
  const activeProducts = setup.products
    .filter((product) => product.active)
    .sort((a, b) => a.sort_order - b.sort_order || a.id - b.id);
  const pickedProducts: Product[] = [];
  const pickedIds = new Set<number>();
  for (const category of defaultCategories) {
    const product = activeProducts.find((candidate) => candidate.category_id === category.id);
    if (product && !pickedIds.has(product.id)) {
      pickedProducts.push(product);
      pickedIds.add(product.id);
    }
    if (pickedProducts.length >= 3) break;
  }
  for (const product of activeProducts) {
    if (pickedProducts.length >= 3) break;
    if (!pickedIds.has(product.id)) {
      pickedProducts.push(product);
      pickedIds.add(product.id);
    }
  }
  if (pickedProducts.length > 1) {
    [pickedProducts[0], pickedProducts[1]] = [pickedProducts[1], pickedProducts[0]];
  }
  const fallbackProducts: Product[] = [
    {
      id: -1,
      name: "Δείγμα προϊόντος",
      print_name: "ΔΕΙΓΜΑ ΠΡΟΪΟΝΤΟΣ",
      category_id: defaultCategories[0]?.id ?? -1,
      aliases: "",
      without_options: "",
      extras: "",
      counter_options: "",
      variation_options: "",
      price: 6.5,
      sort_order: 0,
      active: 1
    },
    {
      id: -2,
      name: "Δεύτερο δείγμα",
      print_name: "ΔΕΥΤΕΡΟ ΔΕΙΓΜΑ",
      category_id: defaultCategories[0]?.id ?? -1,
      aliases: "",
      without_options: "",
      extras: "",
      counter_options: "",
      variation_options: "",
      price: 3,
      sort_order: 1,
      active: 1
    },
    {
      id: -3,
      name: "Τρίτο δείγμα",
      print_name: "ΤΡΙΤΟ ΔΕΙΓΜΑ",
      category_id: defaultCategories[0]?.id ?? -1,
      aliases: "",
      without_options: "",
      extras: "",
      counter_options: "",
      variation_options: "1 1/2",
      price: 2,
      sort_order: 2,
      active: 1
    }
  ];
  const previewSource = pickedProducts.length ? pickedProducts : fallbackProducts;
  const hideUnitVariation = readBool("print_hide_unit_variation", true);
  const variationInQuantity = readBool("print_variation_in_qty", true);
  let previewLines: PreviewLine[] = previewSource.map((product, index) => {
    const variation = splitVariations(product.variation_options)[0] ?? "";
    const name = previewProductName(product, variation, hideUnitVariation, variationInQuantity);
    return {
      product,
      quantity: [2, 1, 3][index] ?? 1,
      name: previewType === "cancel" && index === 0 ? `ΑΦΑΙΡΕΣΗ: ${name}` : name,
      notes: previewProductNotes(product),
      unitPrice: product.price * variationMultiplier(variation)
    };
  });
  if (itemOrderMode !== "selection") {
    previewLines = previewLines.slice().sort((a, b) =>
      (categoryRank.get(a.product.category_id) ?? Number.MAX_SAFE_INTEGER) -
        (categoryRank.get(b.product.category_id) ?? Number.MAX_SAFE_INTEGER) ||
      a.product.sort_order - b.product.sort_order ||
      a.product.id - b.product.id
    );
  }
  const previewIndex = PREVIEW_TYPES.findIndex((type) => type.value === previewType);
  const previewEvent = printableTitle(previewType);
  const previewGeneralNote = previewType === "initial"
    ? ""
    : previewType === "cancel"
      ? "Ακύρωση preview"
      : previewType === "correction"
        ? "Διόρθωση preview"
        : previewType === "history"
          ? "Επανεκτύπωση preview"
          : "Δοκιμή layout";
  const previewTotal = previewLines.reduce(
    (total, line) => total + line.unitPrice * line.quantity,
    0
  );
  const previewTemplate = readKey("print_item_template", "profile", "{qty}x {name}");
  const previewSeparator = readKey("print_separator", "profile", "--------------------------------");
  const headerStyle = readKey("print_header_style", "profile", "table_code");

  return (
    <section className="screen settings-screen" aria-labelledby="settings-heading">
      {back}
      <div className="screen-heading-row">
        <div>
          <h1 id="settings-heading">{t("Εκτύπωση")}</h1>
          <p>
            {selectedPrinter
              ? `${printerName(selectedPrinter)} • ${profile === "history" ? t("Ιστορικό / επανεκτύπωση") : t("Παραγγελία")}`
              : t("Ρυθμίσεις χαρτιού")}
          </p>
        </div>
      </div>

      <div className="settings-groups">
        <section className="settings-group">
          <h2>{t("Εκτυπωτής")}</h2>
          <label className="field">
            <select value={printerId} onChange={(event) => { setPrinterId(Number(event.target.value)); setDrafts({}); }}>
              {printerOptions.map((printer) => (
                <option value={printer.id} key={printer.id}>
                  {printerName(printer)}{printer.enabled ? "" : t(" (ανενεργός)")}
                </option>
              ))}
            </select>
          </label>
          {!printerOptions.length ? <p className="hint-text">{t("Προσθέστε πρώτα έναν εκτυπωτή.")}</p> : null}
        </section>

        <section className="settings-group printer-routing-settings">
          <h2><Route />{t(" Δρομολόγηση κατηγοριών")}</h2>
          <label className="check-option">
            <input
              type="checkbox"
              checked={routeEnabled}
              onChange={(event) => writeGlobalValues({ category_printer_routing_enabled: event.target.checked ? "1" : "0" })}
            />
            <span>{t("Αυτόματη αποστολή κατηγοριών σε εκτυπωτές")}</span>
          </label>
          {routeEnabled ? (
            <>
              <div className="routing-category-list">
                {defaultCategories.map((category) => (
                  <article className="routing-category" key={category.id}>
                    <strong>{category.name}</strong>
                    <div className="check-grid">
                      {printerOptions.map((printer) => (
                        <label className="check-option" key={printer.id}>
                          <input
                            type="checkbox"
                            checked={(categoryRoutes[String(category.id)] ?? []).includes(printer.id)}
                            disabled={!printer.enabled}
                            onChange={(event) => toggleRoutePrinter(category.id, printer.id, event.target.checked)}
                          />
                          <span>{printerName(printer)}{printer.enabled ? "" : t(" (ανενεργός)")}</span>
                        </label>
                      ))}
                    </div>
                  </article>
                ))}
              </div>
              <div className="routing-fallback">
                <strong>{t("Εφεδρικός προορισμός")}</strong>
                <p className="hint-text">{t("Χρησιμοποιείται για κατηγορίες χωρίς δική τους επιλογή.")}</p>
                <div className="check-grid">
                  {printerOptions.map((printer) => (
                    <label className="check-option" key={printer.id}>
                      <input
                        type="checkbox"
                        checked={fallbackPrinterIds.includes(printer.id)}
                        disabled={!printer.enabled}
                        onChange={(event) => {
                          const next = event.target.checked
                            ? [...new Set([...fallbackPrinterIds, printer.id])]
                            : fallbackPrinterIds.filter((id) => id !== printer.id);
                          writeGlobalValues({ category_printer_fallback_ids: next.join(",") });
                        }}
                      />
                      <span>{printerName(printer)}{printer.enabled ? "" : t(" (ανενεργός)")}</span>
                    </label>
                  ))}
                </div>
              </div>
            </>
          ) : null}
        </section>

        <section className="settings-group">
          <h2>{t("Προφίλ εκτύπωσης")}</h2>
          <div className="button-pair">
            <button
              type="button"
              className={profile === "order" ? "secondary-command accent" : "secondary-command"}
              onClick={() => { setProfile("order"); setDrafts({}); }}
            >
              <span>{t("Παραγγελία")}</span>
            </button>
            <button
              type="button"
              className={profile === "history" ? "secondary-command accent" : "secondary-command"}
              onClick={() => { setProfile("history"); setDrafts({}); }}
            >
              <span>{t("Ιστορικό")}</span>
            </button>
          </div>
          <p className="hint-text">
            {profile === "history"
              ? t("Ρυθμίζεις την εκτύπωση από το ιστορικό. Ό,τι δεν αλλάξεις εδώ ακολουθεί το προφίλ παραγγελίας.")
              : t("Ρυθμίζεις το δελτίο που τυπώνεται όταν στέλνεται παραγγελία.")}
          </p>
        </section>

        <section className="settings-group ticket-preview-group">
          <h2>{t("Προεπισκόπηση χαρτιού")}</h2>
          <div className={`ticket-preview paper-${readKey("print_paper_width", "printer", "80")} text-${readKey("print_text_size", "profile", "normal")} spacing-${readKey("print_item_spacing", "profile", "normal")}`}>
            {headerStyle === "event_title" ? (
              <>
                <header>
                  <strong>
                    {readBool("print_show_event_title", true)
                      ? previewEvent
                      : readBool("print_show_table", true)
                        ? `${readKey("print_label_table", "profile", "ΤΡΑΠΕΖΙ")}: 20`
                        : ""}
                  </strong>
                  {readBool("print_show_order_code", true) ? <b>#123</b> : null}
                </header>
                {readBool("print_show_event_title", true) && readBool("print_show_table", true) ? (
                  <h3 className="ticket-table-row">{readKey("print_label_table", "profile", "ΤΡΑΠΕΖΙ")}: 20</h3>
                ) : null}
              </>
            ) : (
              <>
                <header>
                  <strong>
                    {readBool("print_show_table", true)
                      ? `${readKey("print_label_table", "profile", "ΤΡΑΠΕΖΙ")}: 20`
                      : readBool("print_show_event_title", true)
                        ? previewEvent
                        : ""}
                  </strong>
                  {readBool("print_show_order_code", true) ? <b>#123</b> : null}
                </header>
                {readBool("print_show_event_title", true)
                    && readBool("print_show_table", true)
                    && previewEvent !== "ΠΑΡΑΓΓΕΛΙΑ" ? <h3>{previewEvent}</h3> : null}
              </>
            )}
            <div className="ticket-meta">
              {readBool("print_show_time", true) ? <span>{readKey("print_label_time", "profile", "Ώρα")}: 21:42</span> : null}
              {readBool("print_show_waiter", true) ? <span>{readKey("print_label_waiter", "profile", "Σερβιτόρος")}: demo</span> : null}
              {selectedPrinter ? <span>Εκτυπωτής: {printerName(selectedPrinter)}</span> : null}
            </div>
            {readBool("print_show_separators", true) ? <div className="ticket-separator">{previewSeparator}</div> : null}
            <div className="ticket-items">
              {previewLines.map((line, index) => {
                const category = defaultCategories.find((item) => item.id === line.product.category_id);
                const previous = index > 0 ? previewLines[index - 1] : null;
                const showCategory = itemOrderMode === "category_headers"
                  && (!previous || previous.product.category_id !== line.product.category_id);
                const inlineNotes = readBool("print_show_item_notes", true)
                  && previewTemplate.includes("{notes}");
                return (
                  <div className="ticket-item-wrap" key={line.product.id}>
                    {showCategory ? <h4>{category?.name ?? "Κατηγορία"}</h4> : null}
                    <div className={`ticket-item ${inlineNotes ? `detail-${readKey("print_detail_text_size", "profile", "normal")}` : ""}`}>
                      <strong>{previewItemRow(
                        line,
                        previewTemplate,
                        readBool("print_show_item_notes", true),
                        readBool("print_show_item_price", false)
                      )}</strong>
                    </div>
                    {readBool("print_show_item_notes", true) && !previewTemplate.includes("{notes}") ? (
                      <small className={`detail-${readKey("print_detail_text_size", "profile", "normal")}`}>
                        {readKey("print_note_prefix", "profile", "  - ")}{line.notes}
                      </small>
                    ) : null}
                  </div>
                );
              })}
            </div>
            {readBool("print_show_general_notes", true) && previewGeneralNote ? (
              <>
                {readBool("print_show_separators", true) ? <div className="ticket-separator">{previewSeparator}</div> : null}
                <p>{readKey("print_label_comment", "profile", "Σχόλιο")}: {previewGeneralNote}</p>
              </>
            ) : null}
            {readBool("print_show_total", false) ? (
              <>
                {readBool("print_show_separators", true) ? <div className="ticket-separator">{previewSeparator}</div> : null}
                <div className="ticket-total"><span>{readKey("print_label_total", "profile", "Σύνολο")}</span><strong>{ticketMoney(previewTotal)}</strong></div>
              </>
            ) : null}
            {readBool("print_footer_enabled", true) ? (
              <footer>
                {readBool("print_show_separators", true) ? <div className="ticket-separator">{previewSeparator}</div> : null}
                <strong>{readKey("print_label_footer", "profile", "ΤΕΛΟΣ")} {printableFooterTitle(previewEvent)}</strong>
              </footer>
            ) : null}
          </div>
          <div className="preview-test-actions">
            <button type="button" className="secondary-command" disabled={busy || !selectedPrinter?.enabled} onClick={() => selectedPrinter && onProbePrinter(selectedPrinter.id)}>
              <Wifi />
              <span>{t("Έλεγχος σύνδεσης")}</span>
            </button>
            <button type="button" className="secondary-command" disabled={busy || !selectedPrinter?.enabled} onClick={() => selectedPrinter && onTestPrint(selectedPrinter.id)}>
              <Printer />
              <span>{busy ? t("Εκτύπωση...") : t("Δοκιμαστική εκτύπωση")}</span>
            </button>
          </div>
          <h2 className="preview-type-label">{t("Τύπος προεπισκόπησης")}</h2>
          <div className="preview-cycle">
            <button type="button" className="icon-button" onClick={() => setPreviewType(PREVIEW_TYPES[(previewIndex + PREVIEW_TYPES.length - 1) % PREVIEW_TYPES.length].value)} aria-label={t("Προηγούμενος τύπος")}><ArrowLeft /></button>
            <select value={previewType} onChange={(event) => setPreviewType(event.target.value as PreviewType)} aria-label={t("Τύπος προεπισκόπησης")}>
              {PREVIEW_TYPES.map((type) => <option value={type.value} key={type.value}>{t(type.label)}</option>)}
            </select>
            <button type="button" className="icon-button" onClick={() => setPreviewType(PREVIEW_TYPES[(previewIndex + 1) % PREVIEW_TYPES.length].value)} aria-label={t("Επόμενος τύπος")}><ArrowRight /></button>
          </div>
        </section>

        {SELECTS.map((select) => (
          <section className="settings-group" key={select.key}>
            <h2>{t(select.label)}</h2>
            <label className="field">
              <select
                value={readKey(select.key, select.scope, select.fallback)}
                onChange={(event) => writeKey(select.key, select.scope, event.target.value)}
                aria-label={t(select.label)}
              >
                {select.options.map((option) => (
                  <option value={option.value} key={option.value}>
                    {t(option.label)}
                  </option>
                ))}
              </select>
            </label>
            {select.key === "print_item_order" && selectedPrinter ? (
              <button
                type="button"
                className="secondary-command wide print-category-launch"
                onClick={() => setCategoryOrderDraft({
                  order: resolvedCategoryIds.slice(),
                  pinned: pinnedCategoryIds.slice()
                })}
              >
                <span>{t("Σειρά κατηγοριών στο χαρτί")}</span>
              </button>
            ) : null}
          </section>
        ))}

        <section className="settings-group footer-setting-group">
          <label className="check-option">
            <input
              type="checkbox"
              checked={readBool("print_footer_enabled", true)}
              onChange={(event) =>
                writeKey("print_footer_enabled", "profile", event.target.checked ? "1" : "0")
              }
            />
            <span>{t("Τύπωσε γραμμή τέλους στο κάτω μέρος")}</span>
          </label>
        </section>

        <section className="settings-group">
          <h2>{t("Περιεχόμενο χαρτιού")}</h2>
          <div className="option-group check-grid">
            {TOGGLES.map((toggle) => (
              <label className="check-option" key={toggle.key}>
                <input
                  type="checkbox"
                  checked={readBool(toggle.key, toggle.fallback)}
                  onChange={(event) =>
                    writeKey(toggle.key, "profile", event.target.checked ? "1" : "0")
                  }
                />
                <span>{t(toggle.label)}</span>
              </label>
            ))}
          </div>
        </section>

        <section className="settings-group">
          <h2>{t("Επιλογές ποσότητας προϊόντος")}</h2>
          <div className="option-group check-grid">
            {QUANTITY_TOGGLES.map((toggle) => (
              <label className="check-option" key={toggle.key}>
                <input
                  type="checkbox"
                  checked={readBool(toggle.key, toggle.fallback)}
                  onChange={(event) =>
                    writeKey(toggle.key, "profile", event.target.checked ? "1" : "0")
                  }
                />
                <span>{t(toggle.label)}</span>
              </label>
            ))}
          </div>
        </section>

        {profile === "order" && (
          <section className="settings-group">
            <h2>{t("Αυτόματες εκτυπώσεις")}</h2>
            <div className="option-group check-grid">
              <label className="check-option">
                <input
                  type="checkbox"
                  checked={readBool("print_table_change", true)}
                  onChange={(event) =>
                    writeKey("print_table_change", "profile", event.target.checked ? "1" : "0")
                  }
                />
                <span>{t("Δελτίο σε αλλαγή τραπεζιού")}</span>
              </label>
            </div>
          </section>
        )}

        <section className="settings-group">
          <h2>{t("Κείμενα και μορφή")}</h2>
          <p className="hint-text">{t("Στη γραμμή προϊόντος μπορείς να χρησιμοποιήσεις: ")}{"{qty}"}, {"{name}"}, {"{notes}"},{" "}
            {"{price}"}, {"{line_total}"}
          </p>
          {TEXT_FIELDS.map((field) => (
            <label className="field" key={field.key}>
              <span>{t(field.label)}</span>
              <input
                value={drafts[field.key] ?? readKey(field.key, "profile", field.fallback)}
                onChange={(event) =>
                  setDrafts((current) => ({ ...current, [field.key]: event.target.value }))
                }
                onBlur={() => {
                  const draft = drafts[field.key];
                  if (draft !== undefined && draft !== readKey(field.key, "profile", field.fallback)) {
                    writeKey(field.key, "profile", draft);
                  }
                  setDrafts((current) => {
                    const next = { ...current };
                    delete next[field.key];
                    return next;
                  });
                }}
              />
            </label>
          ))}
        </section>
      </div>

      {categoryOrderDraft && selectedPrinter ? (
        <div className="dialog-backdrop">
          <section
            className="details-dialog print-category-dialog"
            role="dialog"
            aria-modal="true"
            aria-label={t("Σειρά για {0}", printerName(selectedPrinter))}
          >
            <PrintCategoryOrder
              title={t("Σειρά για \"{0}\"", printerName(selectedPrinter))}
              categories={defaultCategories}
              initialOrder={categoryOrderDraft.order}
              initialPinned={categoryOrderDraft.pinned}
              onChange={(order, pinned) => setCategoryOrderDraft({
                order: order.slice(),
                pinned: pinned.slice()
              })}
            />
            <footer className="dialog-actions print-category-dialog-actions">
              <button type="button" className="secondary-command" onClick={() => setCategoryOrderDraft(null)}>
                <span>{t("Άκυρο")}</span>
              </button>
              <button
                type="button"
                className="secondary-command danger-command"
                onClick={() => {
                  writeProfileValues({
                    print_category_order: defaultCategories.map((category) => category.id).join(","),
                    print_categories_last: ""
                  });
                  setCategoryOrderDraft(null);
                }}
              >
                <span>{t("Αρχική σειρά")}</span>
              </button>
              <button
                type="button"
                className="primary-command"
                onClick={() => {
                  writeProfileValues({
                    print_category_order: categoryOrderDraft.order.join(","),
                    print_categories_last: categoryOrderDraft.pinned.join(",")
                  });
                  setCategoryOrderDraft(null);
                }}
              >
                <span>{t("Αποθήκευση")}</span>
              </button>
            </footer>
          </section>
        </div>
      ) : null}
    </section>
  );
}
